/**
 * Pocket mode: the arrow, without the screen.
 *
 * Once you trust the arrow, holding a phone in front of your face for four hundred metres stops
 * being the good way to use it. Earbuds in, phone away, and a voice every so often saying
 * "hundred and twenty metres, slightly left" is both calmer and — on a beach at midday, in the
 * rain, or with a child in one arm — the only usable way at all.
 *
 * It is also the accessibility story. Everything the screen says, said aloud, is what makes this
 * product work for somebody who cannot read it.
 *
 * Two rules keep it from being annoying, and both matter more than the feature itself:
 *
 *   - it speaks on a timer, not on every fix, because a fix a second is a monologue
 *   - it says something *new*, or it stays quiet
 *
 * Nothing here reaches the network. `speechSynthesis` is built into the browser, and the voices
 * are the device's own — which is both a privacy property and the reason it works offline.
 */

import { compassPoint, formatDistance, shortestAngleDelta } from '@beeline/shared';

/** How often it is willing to speak. Long enough to walk somewhere between sentences. */
const SPEAK_INTERVAL_MS = 20_000;

/** ...unless something changed enough to be worth interrupting for. */
const URGENT_INTERVAL_MS = 6_000;

export interface GuidanceState {
  distanceMetres: number | null;
  /** Bearing minus heading, in degrees. Null without a compass. */
  relative: number | null;
  /** Bearing in true-north terms, used when there is no heading to be relative to. */
  bearing: number | null;
  name: string | null;
}

export interface Speaker {
  speak: (text: string) => void;
  cancel: () => void;
}

/** The browser's own voice. No network, no account, no third party. */
export const deviceSpeaker: Speaker = {
  speak: (text) => {
    try {
      if (typeof speechSynthesis === 'undefined') return;
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.05;
      // Never queue: an instruction that arrives after you have already turned is worse than
      // no instruction, so a new one always replaces whatever was still being said.
      speechSynthesis.cancel();
      speechSynthesis.speak(utterance);
    } catch {
      // A device with no voices is a device that gets haptics only.
    }
  },
  cancel: () => {
    try {
      speechSynthesis?.cancel();
    } catch {
      /* nothing to cancel */
    }
  },
};

export const speechIsSupported = (): boolean => typeof speechSynthesis !== 'undefined';

/** Turn an angle into the words a walking person can act on without looking. */
export function describeTurn(relativeDegrees: number): string {
  const off = shortestAngleDelta(0, relativeDegrees);
  const magnitude = Math.abs(off);
  const side = off > 0 ? 'right' : 'left';

  if (magnitude <= 15) return 'straight ahead';
  if (magnitude <= 50) return `slightly ${side}`;
  if (magnitude <= 110) return `to your ${side}`;
  if (magnitude <= 150) return `sharply ${side}`;
  return 'behind you';
}

/** The whole sentence. Distance first, because that is what people are actually tracking. */
export function guidanceSentence(state: GuidanceState): string | null {
  if (state.distanceMetres === null) return null;

  const distance = formatDistance(state.distanceMetres)
    .replace(' m', ' metres')
    .replace(' km', ' kilometres');

  if (state.relative !== null) return `${distance}, ${describeTurn(state.relative)}`;
  if (state.bearing !== null) return `${distance}, ${compassPoint(state.bearing)}`;
  return distance;
}

/**
 * Haptic guidance, for when even a voice is too much — or there are no earbuds.
 *
 * One short buzz for left, two for right, a long one for straight ahead. Learnable in about
 * thirty seconds and readable through a coat pocket.
 */
export function turnPattern(relativeDegrees: number): number[] {
  const off = shortestAngleDelta(0, relativeDegrees);
  if (Math.abs(off) <= 15) return [260];
  return off > 0 ? [90, 80, 90] : [90];
}

export interface PocketGuideOptions {
  speaker?: Speaker;
  vibrate?: (pattern: number[]) => void;
  /** Injectable so the pacing can be tested without waiting twenty seconds. */
  now?: () => number;
}

/**
 * Decides when to say something, and what.
 *
 * Kept separate from the live screen so the pacing rules can be reasoned about — and tested —
 * without a browser, a voice, or a person walking down a beach.
 */
export class PocketGuide {
  private lastSpokenAt = 0;
  private lastSaid: string | null = null;
  private lastTurn: string | null = null;
  private running = false;

  private readonly speaker: Speaker;
  private readonly vibrate: (pattern: number[]) => void;
  private readonly now: () => number;

  constructor(options: PocketGuideOptions = {}) {
    this.speaker = options.speaker ?? deviceSpeaker;
    this.vibrate =
      options.vibrate ??
      ((pattern) => {
        try {
          navigator.vibrate?.(pattern);
        } catch {
          /* haptics are a nicety */
        }
      });
    this.now = options.now ?? Date.now;
  }

  get isRunning(): boolean {
    return this.running;
  }

  start(): void {
    this.running = true;
    this.lastSpokenAt = 0;
    this.lastSaid = null;
    this.lastTurn = null;
  }

  stop(): void {
    this.running = false;
    this.speaker.cancel();
  }

  /**
   * Offer the guide the current state. It decides whether that is worth saying out loud.
   *
   * @returns what was said, or null if it stayed quiet.
   */
  update(state: GuidanceState): string | null {
    if (!this.running) return null;

    const sentence = guidanceSentence(state);
    if (!sentence) return null;

    const turn = state.relative === null ? null : describeTurn(state.relative);
    const now = this.now();
    const since = now - this.lastSpokenAt;

    // Drifting off course is the one thing worth interrupting for: it is the only case where
    // waiting the full interval means walking the wrong way for twenty seconds.
    const driftedOff = turn !== null && turn !== this.lastTurn && this.lastTurn !== null;
    const due = since >= (driftedOff ? URGENT_INTERVAL_MS : SPEAK_INTERVAL_MS);

    if (!due) {
      // The haptic still fires on a drift, because it costs nothing and is the whole point of
      // being able to leave the phone in a pocket.
      if (driftedOff && state.relative !== null) this.vibrate(turnPattern(state.relative));
      this.lastTurn = turn;
      return null;
    }

    // Saying the identical sentence twice in a row is noise, not guidance.
    if (sentence === this.lastSaid && !driftedOff) return null;

    this.lastSpokenAt = now;
    this.lastSaid = sentence;
    this.lastTurn = turn;

    this.speaker.speak(state.name ? `${state.name}: ${sentence}` : sentence);
    if (state.relative !== null) this.vibrate(turnPattern(state.relative));

    return sentence;
  }
}
